import {RefObject, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {PRINT_PAGE_PADDING, PRINT_PAGE_SIZES} from "./constants";
import {ServerTablePrintOrientation} from "./types";
import {countOversizedPrintRows, packRowsIntoPages} from "./utils";

interface PrintPageMetrics {
    headerHeight: number
    theadHeight: number
    rowHeights: number[]
}

interface UseServerTablePrintPaginationOptions {
    measureRef: RefObject<HTMLDivElement>
    orientation: ServerTablePrintOrientation
    layoutDependencies: readonly unknown[]
}

// Browser page breaks are reproduced from measured rows and fixed A4 page budgets.
export function useServerTablePrintPagination({
    measureRef,
    orientation,
    layoutDependencies
}: UseServerTablePrintPaginationOptions) {
    const [metrics, setMetrics] = useState<PrintPageMetrics | null>(null);
    const [fontsReady, setFontsReady] = useState(false);

    // Font swaps can change row heights, so pagination remeasures once fonts settle.
    useEffect(() => {
        let active = true;
        document.fonts?.ready.then(() => {
            if (active) setFontsReady(true);
        });
        return () => {
            active = false;
        };
    }, []);

    // Measuring before paint prevents a stale page split from flashing.
    useLayoutEffect(() => {
        const root = measureRef.current;
        if (!root) return;

        const heightOf = (selector: string) =>
            root.querySelector(selector)?.getBoundingClientRect().height ?? 0;

        setMetrics({
            headerHeight: heightOf('[data-print-header]'),
            theadHeight: heightOf('[data-print-thead]'),
            rowHeights: Array.from(
                root.querySelectorAll('[data-print-row]'),
                row => row.getBoundingClientRect().height
            )
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measureRef, orientation, fontsReady, ...layoutDependencies]);

    const pagination = useMemo(() => {
        if (!metrics) return {pages: null, oversizedRowCount: 0};

        // Every page reserves the repeated table header and table border.
        const pageBudget = PRINT_PAGE_SIZES[orientation].height
            - 2 * PRINT_PAGE_PADDING
            - metrics.theadHeight
            - 2;

        const firstPageBudget = pageBudget - metrics.headerHeight;
        const pages = packRowsIntoPages(metrics.rowHeights, firstPageBudget, pageBudget);

        return {
            pages,
            oversizedRowCount: countOversizedPrintRows(
                metrics.rowHeights,
                pages,
                firstPageBudget,
                pageBudget
            )
        };
    }, [metrics, orientation]);

    return pagination;
}
